import React, { useState, useEffect, useRef } from 'react';
import { Medication } from './types';
import { loadMedications, filterMedications, FREQUENCY_OPTIONS, getUnitForMedication, getWZJGMedications } from './data/medications';

interface MedicationFormProps {
  onSubmit: (data: Medication) => void;
  initialData?: Medication;
  onCancel?: () => void;
}

export const MedicationForm: React.FC<MedicationFormProps> = ({ onSubmit, initialData, onCancel }) => {
  const now = new Date();
  const [selectedMedication, setSelectedMedication] = useState<string>(initialData?.name || '');
  const [customMedication, setCustomMedication] = useState<string>('');
  const [isCustomMedication, setIsCustomMedication] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [filteredMedications, setFilteredMedications] = useState<Array<{name: string, unit: string}>>([]);
  const [customFrequency, setCustomFrequency] = useState<string>('');
  const searchRef = useRef<HTMLDivElement>(null);

  // Załaduj leki przy montowaniu komponentu
  useEffect(() => {
    loadMedications().then(meds => {
      setFilteredMedications(meds);
    });
  }, []);
  
  const [formData, setFormData] = useState({
    date: initialData?.date || now.toISOString().split('T')[0],
    time: initialData?.time || now.toTimeString().slice(0, 5),
    name: initialData?.name || '',
    dosage: initialData?.dosage || 1,
    frequency: initialData?.frequency || '1x dziennie',
    withMeal: initialData?.withMeal ?? false,
    taken: initialData?.taken ?? false,
    notes: initialData?.notes || ''
  });

  // Sprawdź czy początkowy lek jest na liście
  useEffect(() => {
    if (initialData?.name) {
      const medications = getWZJGMedications();
      const isInList = medications.some(med => med.name === initialData?.name);
      if (!isInList) {
        setIsCustomMedication(true);
        setCustomMedication(initialData.name);
        setSearchTerm(initialData.name);
      } else {
        setSelectedMedication(initialData.name);
        setSearchTerm(initialData.name);
      }
      
      // Sprawdź czy częstotliwość jest niestandardowa
      if (initialData.frequency && !FREQUENCY_OPTIONS.includes(initialData.frequency)) {
        setCustomFrequency(initialData.frequency);
        setFormData(prev => ({ ...prev, frequency: 'inne' }));
      } else if (initialData.frequency && FREQUENCY_OPTIONS.includes(initialData.frequency)) {
        setFormData(prev => ({ ...prev, frequency: initialData.frequency }));
      }
      
      // Upewnij się, że dosage jest liczbą
      if (initialData.dosage) {
        const dosageNum = typeof initialData.dosage === 'string' ? parseInt(initialData.dosage) || 1 : initialData.dosage;
        setFormData(prev => ({ ...prev, dosage: dosageNum }));
      }
    }
  }, [initialData]);

  // Filtruj leki na podstawie wyszukiwania
  useEffect(() => {
    // Nie pokazuj sugestii jeśli lek został już wybrany z listy
    if (selectedMedication && searchTerm === selectedMedication) {
      setShowSuggestions(false);
      return;
    }
    
    if (searchTerm.trim()) {
      const filtered = filterMedications(searchTerm);
      setFilteredMedications(filtered);
      // Pokazuj sugestie tylko jeśli użytkownik aktywnie wpisuje (nie wybrał jeszcze leku)
      if (!selectedMedication || searchTerm !== selectedMedication) {
        setShowSuggestions(true);
      }
    } else {
      const medications = getWZJGMedications();
      setFilteredMedications(medications);
      setShowSuggestions(false);
    }
  }, [searchTerm, selectedMedication]);

  // Zamknij sugestie przy kliknięciu poza komponentem
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const medicationName = isCustomMedication ? customMedication.trim() : selectedMedication;
    
    if (!medicationName) {
      alert('Proszę wybrać lub wpisać nazwę leku');
      return;
    }

    const finalFrequency = formData.frequency === 'inne' ? customFrequency.trim() : formData.frequency;
    
    if (!finalFrequency) {
      alert('Proszę wybrać lub wpisać częstotliwość');
      return;
    }

    const dataToSave: Medication = {
      id: initialData?.id || Date.now().toString(),
      date: formData.date,
      time: formData.time,
      name: medicationName,
      dosage: formData.dosage,
      frequency: finalFrequency,
      withMeal: formData.withMeal,
      taken: formData.taken,
      notes: formData.notes.trim()
    };
    onSubmit(dataToSave);
    
    // Reset form tylko jeśli nie edytujemy
    if (!initialData) {
      const now = new Date();
      setFormData({
        date: now.toISOString().split('T')[0],
        time: now.toTimeString().slice(0, 5),
        name: '',
        dosage: 1,
        frequency: '1x dziennie',
        withMeal: false,
        taken: false,
        notes: ''
      });
      setSelectedMedication('');
      setCustomMedication('');
      setIsCustomMedication(false);
      setSearchTerm('');
      setCustomFrequency('');
    }
  };

  const handleMedicationSelect = (medicationName: string) => {
    setIsCustomMedication(false);
    setSelectedMedication(medicationName);
    setCustomMedication('');
    setSearchTerm(medicationName);
    setShowSuggestions(false);
    // Upewnij się, że lista nie pokazuje się ponownie
    setTimeout(() => {
      setShowSuggestions(false);
    }, 100);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    
    // Jeśli użytkownik zmienia tekst po wybraniu leku, resetuj wybór
    if (selectedMedication && value !== selectedMedication) {
      setSelectedMedication('');
      setIsCustomMedication(false);
      setCustomMedication('');
    }
    
    // Sprawdź czy wpisany tekst pasuje dokładnie do jakiegoś leku
    const medications = getWZJGMedications();
    const exactMatch = medications.find(med => 
      med.name.toLowerCase() === value.toLowerCase().trim()
    );
    
    if (exactMatch && value.trim() === exactMatch.name) {
      handleMedicationSelect(exactMatch.name);
    } else if (value.trim() && !exactMatch) {
      setIsCustomMedication(true);
      setSelectedMedication('');
      setCustomMedication(value);
      setFormData(prev => ({ ...prev, name: value }));
    }
  };

  const handleCustomOption = () => {
    setIsCustomMedication(true);
    setSelectedMedication('');
    setCustomMedication(searchTerm);
    setShowSuggestions(false);
  };

  const selectedUnit = selectedMedication ? getUnitForMedication(selectedMedication) : 'sztuka';

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: '600px', margin: '20px 0' }}>
      <h2>Lek</h2>
      
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Data:</label>
        <input
          type="date"
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          required
          style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Godzina:</label>
        <input
          type="time"
          value={formData.time}
          onChange={(e) => setFormData({ ...formData, time: e.target.value })}
          required
          style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }} ref={searchRef}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Nazwa leku:</label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => {
              // Pokazuj sugestie tylko jeśli nie ma wybranego leku lub tekst się zmienił
              if (searchTerm.trim() && (!selectedMedication || searchTerm !== selectedMedication)) {
                setShowSuggestions(true);
              }
            }}
            placeholder="Wpisz nazwę leku lub wybierz z listy..."
            required
            style={{ 
              width: '100%', 
              padding: '8px', 
              borderRadius: '5px', 
              border: '1px solid #ddd',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
          
          {showSuggestions && filteredMedications.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '4px',
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '5px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 1000
            }}>
              {filteredMedications.slice(0, 10).map(med => (
                <div
                  key={med.name}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleMedicationSelect(med.name);
                  }}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <div style={{ fontWeight: '500', fontSize: '14px' }}>{med.name}</div>
                </div>
              ))}
              {filteredMedications.length > 10 && (
                <div style={{
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#666',
                  fontStyle: 'italic',
                  borderTop: '1px solid #eee'
                }}>
                  ... i {filteredMedications.length - 10} więcej
                </div>
              )}
              {/* Pokaż opcję "własny lek" tylko jeśli wpisany tekst nie pasuje dokładnie do żadnego leku */}
              {!filteredMedications.some(med => med.name.toLowerCase() === searchTerm.toLowerCase().trim()) && searchTerm.trim() && (
                <div
                  onClick={handleCustomOption}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    borderTop: '2px solid #4CAF50',
                    backgroundColor: '#f9f9f9',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    color: '#4CAF50'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9f9f9';
                  }}
                >
                  ➕ Użyj "{searchTerm}" jako własny lek
                </div>
              )}
            </div>
          )}
          
          {showSuggestions && filteredMedications.length === 0 && searchTerm.trim() && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '4px',
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '5px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              padding: '12px',
              zIndex: 1000
            }}>
              <div style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>
                Nie znaleziono leku "{searchTerm}"
              </div>
              <div
                onClick={handleCustomOption}
                style={{
                  padding: '10px 12px',
                  cursor: 'pointer',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  borderRadius: '5px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#45a049';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#4CAF50';
                }}
              >
                ➕ Dodaj "{searchTerm}" jako własny lek
              </div>
            </div>
          )}
        </div>
        
        {isCustomMedication && selectedMedication === '' && (
          <div style={{ 
            marginTop: '8px', 
            padding: '8px', 
            backgroundColor: '#fff3cd', 
            borderRadius: '5px',
            fontSize: '12px',
            color: '#856404'
          }}>
            ⚠️ Wprowadzasz własny lek. Upewnij się, że nazwa jest poprawna.
          </div>
        )}
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Liczba sztuk na raz:
        </label>
        <input
          type="number"
          min="1"
          max="20"
          value={formData.dosage}
          onChange={(e) => setFormData({ ...formData, dosage: parseInt(e.target.value) || 1 })}
          required
          style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
        />
        {selectedMedication && (
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            Jednostka: {selectedUnit}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Częstotliwość:</label>
        <select
          value={formData.frequency}
          onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
          required
          style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px' }}
        >
          {FREQUENCY_OPTIONS.map(freq => (
            <option key={freq} value={freq}>{freq}</option>
          ))}
        </select>
        
        {formData.frequency === 'inne' && (
          <input
            type="text"
            value={customFrequency}
            onChange={(e) => setCustomFrequency(e.target.value)}
            placeholder="Wpisz częstotliwość (np. co 8 godzin, przed posiłkiem)"
            required
            style={{ 
              width: '100%', 
              padding: '8px', 
              borderRadius: '5px', 
              border: '1px solid #ddd',
              marginTop: '8px'
            }}
          />
        )}
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={formData.withMeal}
            onChange={(e) => setFormData({ ...formData, withMeal: e.target.checked })}
            style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span style={{ fontWeight: 'bold' }}>W trakcie posiłku</span>
        </label>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={formData.taken}
            onChange={(e) => setFormData({ ...formData, taken: e.target.checked })}
            style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span style={{ fontWeight: 'bold' }}>Lek został przyjęty</span>
        </label>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Notatki (opcjonalnie):</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Dodatkowe informacje o leku..."
          rows={3}
          style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontFamily: 'inherit' }}
        />
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        <button
          type="submit"
          style={{
            padding: '12px 24px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            flex: 1
          }}
        >
          {initialData ? 'Zaktualizuj' : 'Dodaj lek'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '12px 24px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            Anuluj
          </button>
        )}
      </div>
    </form>
  );
};
